import dedupeThreadEventsByIdentity from '@/lib/threadEvents/dedupeThreadEventsByIdentity.js';

const RUN_DURATION_DIVIDER_KIND = 'run_duration';
// An assistant bubble with this id prefix is local and provisional: it sorts below its run's other events until the real reply replaces it.
export const PENDING_ASSISTANT_EVENT_ID_PREFIX = 'pending-assistant-';

const toSafeSortString = (value) => {
  return String(value || '').trim();
};

const getEventSeq = (event) => {
  const seq = Number(event?.seq);

  if (Number.isFinite(seq) && seq > 0) {
    return seq;
  }

  return null;
};

const isAssistantBubbleEvent = (event) => {
  if (event?.type !== 'bubble') {
    return false;
  }

  return toSafeSortString(event?.className).toLowerCase() !== 'user';
};

const isPendingAssistantBubbleEvent = (event) => {
  if (!isAssistantBubbleEvent(event)) {
    return false;
  }

  return toSafeSortString(event?.id).startsWith(PENDING_ASSISTANT_EVENT_ID_PREFIX);
};

const comparePendingAssistantPlacement = (left, right) => {
  const leftRunId = toSafeSortString(left?.runId);
  const rightRunId = toSafeSortString(right?.runId);

  if (!leftRunId || leftRunId !== rightRunId) {
    return 0;
  }

  const leftIsPendingAssistantBubble = isPendingAssistantBubbleEvent(left);
  const rightIsPendingAssistantBubble = isPendingAssistantBubbleEvent(right);

  if (leftIsPendingAssistantBubble === rightIsPendingAssistantBubble) {
    return 0;
  }

  return leftIsPendingAssistantBubble ? 1 : -1;
};

// Reasoning events stream live with no seq (the server ids them `run-reasoning:<runId>:...`)
// and later persist under the same id WITH a seq. While seq-less, the plain seq rules would
// sink them below every persisted event — the thinking, which happens FIRST, would render at
// the bottom of the conversation. They carry an accurate createdAt (first delta time), so a
// seq-less reasoning event orders chronologically against everything; once the persisted seq
// arrives it uses the normal seq rules like any other event.
const isSeqlessRunReasoningEvent = (event) => {
  return toSafeSortString(event?.id).startsWith('run-reasoning:') && getEventSeq(event) === null;
};

const sortByBaseEventOrder = (events = []) => {
  return [...events].sort((left, right) => {
    const pendingAssistantSort = comparePendingAssistantPlacement(left, right);

    if (pendingAssistantSort !== 0) {
      return pendingAssistantSort;
    }

    // Live (not yet persisted) reasoning events bypass the seq rules and fall through to the
    // createdAt comparison so the thinking sits where it happened in the run.
    const eitherIsRunReasoning = isSeqlessRunReasoningEvent(left) || isSeqlessRunReasoningEvent(right);

    const leftSeq = getEventSeq(left);
    const rightSeq = getEventSeq(right);

    if (!eitherIsRunReasoning && leftSeq !== null && rightSeq !== null && leftSeq !== rightSeq) {
      return leftSeq - rightSeq;
    }

    if (!eitherIsRunReasoning && leftSeq !== null && rightSeq === null) {
      return -1;
    }

    if (!eitherIsRunReasoning && leftSeq === null && rightSeq !== null) {
      return 1;
    }

    if (leftSeq === null && rightSeq === null) {
      const leftRunId = toSafeSortString(left?.runId);
      const rightRunId = toSafeSortString(right?.runId);

      if (leftRunId && leftRunId === rightRunId) {
        const leftIsUser = toSafeSortString(left?.className).toLowerCase() === 'user';
        const rightIsUser = toSafeSortString(right?.className).toLowerCase() === 'user';

        if (leftIsUser !== rightIsUser) {
          return leftIsUser ? -1 : 1;
        }
      }
    }

    const leftCreatedAt = Date.parse(left?.createdAt || 0);
    const rightCreatedAt = Date.parse(right?.createdAt || 0);

    if (Number.isFinite(leftCreatedAt) && Number.isFinite(rightCreatedAt) && leftCreatedAt !== rightCreatedAt) {
      return leftCreatedAt - rightCreatedAt;
    }

    return toSafeSortString(left?.id).localeCompare(toSafeSortString(right?.id));
  });
};

const sortPieveActivityEvents = (events = []) => {
  const canonicalEvents = dedupeThreadEventsByIdentity({
    events
  }).filter((event) => {
    return toSafeSortString(event?.dividerKind).toLowerCase() !== RUN_DURATION_DIVIDER_KIND;
  });
  return sortByBaseEventOrder(canonicalEvents);
};

export default sortPieveActivityEvents;
